import { themedConfirm } from '../../../lib/themed-dialogs';
// AppHost Module — deploy a správa Laravel, React a Node.js aplikací
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api';

import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';

let _widgetScope: EffectiveScope = { node: null, identity: null };
export function setApphostWidgetScope(scope: EffectiveScope) {
  _widgetScope = scope;
}
function callMcp<T = unknown>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  return callWidgetTool(name, args, _widgetScope) as Promise<T>;
}
function scopeKey() {
  return _widgetScope.node ?? 'local';
}
import LovableControlPanel from '../apphost-lovable';
import {
  BackButton,
  BackupPanel,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DeployLogViewer,
  EmptyState,
  Input,
  Label,
  LifecycleControls,
  Modal,
  RefreshButton,
  Select,
  StatCard,
  StatsGrid,
  StatusDot,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Toggle,
} from '../../../components/ui';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Database,
  Download,
  ExternalLink,
  FileText,
  GitBranch,
  Globe,
  Layers,
  Lock,
  Network,
  Pencil,
  Plus,
  RefreshCw,
  Server,
  Settings,
  Terminal,
  Trash2,
  Upload,
  Wand2,
  X,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import WorkspaceWorkPanel from '../../../components/WorkspaceWorkPanel';
import { plannedStack, StackFlowGraphic } from '../../../components/StackFlowGraphic';
import ReleaseCarousel from '../../../components/ReleaseCarousel';

// ── Types ─────────────────────────────────────────────────────────────────────

type AppType = 'laravel' | 'react' | 'node';
type RedisMode = 'none' | 'container' | 'shared';
type DbMode = 'none' | 'postgres' | 'mysql' | 'external';
type NginxMode = 'npm' | 'internal' | 'none';

interface ContainerStatus {
  app: string;
  redis: string;
  db: string;
  nginx: string;
}

/**
 * Položka z `apphost.lovable.list` — jen pole, která tenhle panel renderuje.
 * Plný tvar viz modules/apphost/tools/apphost.lovable.list.ts.
 */
interface LovableBinding {
  id: string;
  apphost_name: string;
  project_id: string | null;
  tier: 'sandbox' | 'staging' | 'production';
  state:
    | 'analyzed'
    | 'provisioned'
    | 'schema_imported'
    | 'data_imported'
    | 'deployed'
    | 'approved'
    | 'backed_up';
  origin_repo: string;
  git_branch: string;
  cloud_supabase_url: string;
  has_service_role: boolean;
  has_jwt_secret: boolean;
  has_git_token: boolean;
  is_adopted: boolean;
  applied_migrations_count: number;
  last_data_pull_at: string | null;
  updated_at: string;
}

interface AppInstance {
  name: string;
  type: AppType;
  status: string;
  app_port: number | null;
  redis_mode: RedisMode;
  db_mode: DbMode;
  nginx_mode: NginxMode;
  domain: string | null;
  /**
   * Vazba workloadu na projekt. Projektová část má jedinou legální cestu
   * k akci — `<projekt>.apphost.<action>` s `service`; obecné `{name:…}`
   * server zamítne. Nástroj tohle pole řekne, UI si z něj skládá cestu.
   */
  project?: { id: string; service: string } | null;
  /** Náhledová (blue) URL — trvalý objekt se stavem on/off. */
  preview_enabled?: boolean;
  preview_domain?: string | null;
  preview_port?: number | null;
  git_repo: string | null;
  git_branch: string;
  containers: ContainerStatus;
  ports?: {
    app?: number;
    redis?: number;
    db?: number;
  };
  created_at: string;
  // přidání portů a stavu kontejneru pro zobrazení v UI
  container_status?: string;
  port_status?: Array<{ port: number; status: 'open' | 'closed' }>;
  // porty a kontejnery z Supabase UI
  total?: number;
  running?: number;
  healthy?: number;
  unhealthy?: number;
  // publish state
  private_api: string | null;
  private_studio: string | null;
  url_hash: string | null;
  api_public: boolean;
  studio_public: boolean;
  public_api: string | null;
  public_studio: string | null;
  // cert info
  api_cert: { expires_on: string; days_left: number; valid: boolean } | null;
  studio_cert: { expires_on: string; days_left: number; valid: boolean } | null;
}


// ── Status helpers ────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<AppType, string> = {
  laravel: 'text-red-400 bg-red-500/10',
  react: 'text-blue-400 bg-blue-500/10',
  node: 'text-green-400 bg-green-500/10',
};

// ── Instance card ─────────────────────────────────────────────────────────────

function InstanceCard({ inst, onSelect }: { inst: AppInstance; onSelect: () => void }) {
  const { t } = useTranslation();
  // For managing pending actions
  const [pendingAction, setPendingAction] = useState<Record<string, string>>({});
  const [logsOpen, setLogsOpen] = useState(false);
  const qc = useQueryClient();

  const manageMutate = useMutation({
    mutationFn: ({ name, action }: { name: string; action: string }) => {
      setPendingAction((p) => ({ ...p, [name]: action }));
      return callMcp<{ ok: boolean; error?: string }>('apphost.manage', { name, action });
    },
    onSuccess: (r, { name, action }) => {
      setPendingAction((p) => {
        const n = { ...p };
        delete n[name];
        return n;
      });
      if (!r.ok) {
        toast.error(r.error ?? t('apphost.actionFailed', { action }));
        return;
      }
      toast.success(t('apphost.actionOk', { action }));
      qc.invalidateQueries({ queryKey: ['apphost-status', scopeKey(), name] });
      qc.invalidateQueries({ queryKey: ['apphost-list', scopeKey()] });
    },
    onError: (e, { name }) => {
      setPendingAction((p) => {
        const n = { ...p };
        delete n[name];
        return n;
      });
      toast.error((e as Error).message);
    },
  });

  const delMutate = useMutation({
    mutationFn: (name: string) =>
      callMcp<{ ok: boolean; error?: string }>('apphost.delete', { name }),
    onSuccess: (r, name) => {
      if (!r.ok) {
        toast.error(r.error ?? t('apphost.deleteFailed'));
        return;
      }
      toast.success(t('apphost.instanceDeleted', { name }));
      qc.invalidateQueries({ queryKey: ['apphost-list', scopeKey()] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const pending = pendingAction[inst.name];
  const isActive = inst.status === 'running';

  return (
    <div
      onClick={onSelect}
      className='group border border-gray-800 rounded-xl p-4 hover:border-gray-600 cursor-pointer transition-colors bg-gray-900/20 hover:bg-gray-900/40'
    >
      <div className='flex items-start justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <StatusDot status={inst.status} />
          <span className='font-bold text-white text-sm'>{inst.name}</span>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-medium ${
              TYPE_COLORS[inst.type]
            }`}
          >
            {inst.type}
          </span>
        </div>
        {inst.app_port && <span className='text-xs text-gray-500 font-mono'>:{inst.app_port}</span>}
      </div>
      <div className='flex items-center gap-3 text-[10px] text-gray-500'>
        {inst.domain && (
          <span className='flex items-center gap-1'>
            <Globe size={9} />
            {inst.domain}
          </span>
        )}
        {inst.db_mode !== 'none' && (
          <span className='flex items-center gap-1'>
            <Database size={9} />
            {inst.db_mode}
          </span>
        )}
        {inst.redis_mode !== 'none' && (
          <span className='flex items-center gap-1'>
            <Layers size={9} />redis
          </span>
        )}
        {inst.git_repo && (
          <span className='flex items-center gap-1 truncate max-w-[140px]'>
            <GitBranch size={9} />
            {inst.git_branch}
          </span>
        )}
      </div>
      <div className='flex gap-1 mt-2'>
        {(['app', 'redis', 'db'] as const).filter((s) => inst.containers[s] !== 'missing').map(
          (s) => (
            <span
              key={s}
              className={`text-[9px] px-1 py-0.5 rounded font-mono ${
                inst.containers[s] === 'running'
                  ? 'bg-green-500/10 text-green-400'
                  : 'bg-gray-800 text-gray-600'
              }`}
            >
              {s}
            </span>
          ),
        )}
      </div>
      <div className='flex items-center gap-3 mt-3'>
        <div className='flex-1'>
          <div className='text-[10px] text-gray-500 flex items-center gap-2'>
            {inst.ports?.app && <span className='font-mono'>app: {inst.ports.app}</span>}
            {inst.ports?.redis && <span className='font-mono'>redis: {inst.ports.redis}</span>}
            {inst.ports?.db && <span className='font-mono'>db: {inst.ports.db}</span>}
          </div>
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant='ghost'
            size='icon'
            onClick={(e) => {
              e.stopPropagation();
              setLogsOpen(true);
            }}
            title={t('apphost.deployLog')}
            icon={<FileText size={13} />}
          />
          <Button
            variant='ghost'
            size='icon'
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            title={t('apphost.detailSettings')}
            icon={<Settings size={13} />}
          />
          <div
            className='opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1'
            onClick={(e) => e.stopPropagation()}
          >
            <LifecycleControls.Row
              name={inst.name}
              running={isActive}
              pending={pending}
              onAction={(name, action) => {
                if (action === 'delete') delMutate.mutate(name);
                else manageMutate.mutate({ name, action });
              }}
            />
          </div>
        </div>
      </div>
      <DeployLogViewer
        open={logsOpen}
        onClose={() => setLogsOpen(false)}
        module='apphost'
        instance={inst.name}
      />
    </div>
  );
}

// ── Instance Detail ───────────────────────────────────────────────────────────

function InstanceDetail({ name, onBack }: { name: string; onBack: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: statusData, isLoading, refetch } = useQuery({
    queryKey: ['apphost-status', scopeKey(), name],
    queryFn: () =>
      callMcp('apphost.status', { name }) as Promise<
        { ok: boolean } & AppInstance & {
          app_stats: Record<string, unknown>;
          git: Record<string, unknown>;
        }
      >,
    refetchInterval: 15_000,
  });

  const manageMut = useMutation({
    mutationFn: (action: string) => callMcp('apphost.manage', { name, action }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apphost-status', scopeKey(), name] });
      qc.invalidateQueries({ queryKey: ['apphost-list', scopeKey()] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const deployMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => callMcp('apphost.deploy', { name, ...body }),
    onSuccess: (r: unknown) => {
      toast.success(t('apphost.deployDone'));
      qc.invalidateQueries({ queryKey: ['apphost-status', scopeKey(), name] });
      console.log(r);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='h-8 bg-gray-800/40 rounded-xl animate-pulse' />
        <div className='h-10 bg-gray-800/40 rounded-xl animate-pulse' />
        <div className='h-64 bg-gray-800/40 rounded-xl animate-pulse' />
      </div>
    );
  }
  if (!statusData?.ok) {
    return (
      <EmptyState
        icon={<AlertCircle size={24} />}
        title={t('apphost.instanceNotFound')}
      />
    );
  }

  const inst = statusData;

  return (
    <div className='space-y-5'>
      <div className='flex items-center gap-3'>
        <BackButton onClick={onBack} label={t('apphost.back')} crumb={name} />
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${TYPE_COLORS[inst.type]}`}>
          {inst.type}
        </span>
        <div className='ml-auto flex items-center gap-2'>
          <RefreshButton onRefresh={() => refetch()} size='md' />
          <LifecycleControls.Bar
            name={name}
            running={inst.status === 'running'}
            loading={manageMut.isPending}
            onAction={(action) => manageMut.mutate(action)}
          />
        </div>
      </div>

      <WorkspaceWorkPanel
        scope={{ kind: 'instance', instance: name }}
        callTool={(tool, args) => callMcp(tool, args)}
      />

      <Tabs defaultTab='overview'>
        <TabList>
          <Tab id='overview' icon={<Server size={14} />}>{t('apphost.tabOverview')}</Tab>
          <Tab id='config' icon={<Settings size={14} />}>{t('apphost.tabConfig')}</Tab>
          <Tab id='git' icon={<GitBranch size={14} />}>{t('apphost.tabGitDeploy')}</Tab>
          <Tab id='lovable' icon={<Wand2 size={14} />}>{t('apphost.tabLovable')}</Tab>
          <Tab id='exec' icon={<Terminal size={14} />}>{t('apphost.tabTerminal')}</Tab>
          <Tab id='logs' icon={<FileText size={14} />}>{t('apphost.tabLogs')}</Tab>
          <Tab id='backup' icon={<Download size={14} />}>{t('apphost.tabBackup')}</Tab>
        </TabList>

        <TabPanel id='overview'>
          <OverviewPanel
            inst={inst}
            onChanged={() => {
              qc.invalidateQueries({ queryKey: ['apphost-status', scopeKey(), name] });
              qc.invalidateQueries({ queryKey: ['apphost-list', scopeKey()] });
            }}
          />
        </TabPanel>
        <TabPanel id='config'>
          <ConfigPanel name={name} />
        </TabPanel>
        <TabPanel id='git'>
          <GitPanel
            name={name}
            inst={inst}
            onDeploy={(body) => deployMut.mutate(body)}
            deploying={deployMut.isPending}
          />
        </TabPanel>
        <TabPanel id='lovable'>
          <InstanceLovablePanel name={name} />
        </TabPanel>
        <TabPanel id='exec'>
          <ExecPanel name={name} type={inst.type} />
        </TabPanel>
        <TabPanel id='logs'>
          <LogsPanel name={name} />
        </TabPanel>
        <TabPanel id='backup'>
          <BackupPanel module='apphost' instanceName={name} containerPrefix={`apphost-${name}`} />
        </TabPanel>
      </Tabs>
    </div>
  );
}

function OverviewPanel(
  { inst }: {
    inst: AppInstance & { app_stats?: Record<string, unknown>; git?: Record<string, unknown> };
    onChanged?: () => void;
  },
) {
  const { t } = useTranslation();
  return (
    <div className='space-y-4'>
      <StatsGrid>
        <StatCard
          label={t('apphost.status')}
          value={inst.status}
          icon={CheckCircle2}
          variant={inst.status === 'running' ? 'success' : undefined}
        />
        <StatCard
          label={t('apphost.appPort')}
          value={inst.app_port ? `:${inst.app_port}` : '—'}
          icon={Server}
        />
        <StatCard label={t('apphost.redis')} value={inst.redis_mode} icon={Layers} />
        <StatCard label={t('apphost.database')} value={inst.db_mode} icon={Database} />
      </StatsGrid>

      {
        /* Resources link — limity (CPU, RAM, IOPS, network) se spravují v Resources tab.
         ContainerStore JSON je zdroj pravdy, apphost má sync helper pro vidět změny. */
      }
      <Card>
        <CardContent className='p-4'>
          <div className='flex items-center justify-between gap-4'>
            <div>
              <div className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                {t('apphost.resourceLimits')}
              </div>
              <div className='text-[11px] text-gray-500 mt-1'>
                {t('apphost.resourceLimitsDesc')}
              </div>
            </div>
            <a
              href={`/core/toolbox?tab=resources&container=apphost-${inst.name}`}
              className='text-xs px-3 py-1.5 rounded-xl border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 transition-colors whitespace-nowrap'
            >
              {t('apphost.manageResources')} →
            </a>
          </div>
        </CardContent>
      </Card>

      {inst.ports && (
        <Card>
          <CardHeader>
            <CardTitle icon={Network}>{t('apphost.ports')}</CardTitle>
          </CardHeader>
          <CardContent className='p-4'>
            <div className='space-y-2'>
              {inst.ports.app && (
                <div className='flex items-center justify-between'>
                  <span className='text-xs font-mono text-gray-400'>{t('apphost.appPort')}</span>
                  <span className='text-xs font-mono'>{inst.ports.app}</span>
                </div>
              )}
              {inst.ports.redis && (
                <div className='flex items-center justify-between'>
                  <span className='text-xs font-mono text-gray-400'>{t('apphost.redisPort')}</span>
                  <span className='text-xs font-mono'>{inst.ports.redis}</span>
                </div>
              )}
              {inst.ports.db && (
                <div className='flex items-center justify-between'>
                  <span className='text-xs font-mono text-gray-400'>
                    {t('apphost.databasePort')}
                  </span>
                  <span className='text-xs font-mono'>{inst.ports.db}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle icon={Server}>{t('apphost.containers')}</CardTitle>
        </CardHeader>
        <CardContent className='p-4'>
          <div className='space-y-2'>
            {(['app', 'redis', 'db', 'nginx'] as const).map((svc) => {
              const s = inst.containers[svc];
              if (s === 'missing') return null;
              return (
                <div key={svc} className='flex items-center justify-between'>
                  <span className='text-xs font-mono text-gray-400'>apphost-{inst.name}-{svc}</span>
                  <StatusDot status={s} withLabel />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {inst.port_status && (
        <Card>
          <CardHeader>
            <CardTitle icon={Zap}>{t('apphost.portStatus')}</CardTitle>
          </CardHeader>
          <CardContent className='p-4'>
            <div className='space-y-2'>
              {inst.port_status.map(({ port, status }) => (
                <div key={port} className='flex items-center justify-between'>
                  <span className='text-xs font-mono text-gray-400'>
                    {t('apphost.port', { port })}
                  </span>
                  <Badge variant={status === 'open' ? 'green' : 'red'}>{status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Adresy — interní + veřejná */}
      {inst.private_api && (
        <Card>
          <CardHeader>
            <CardTitle icon={Globe}>{t('apphost.addresses')}</CardTitle>
          </CardHeader>
          <CardContent className='p-4'>
            <div className='text-xs space-y-0.5'>
              {/* API — interní */}
              <div className='flex items-center gap-2 flex-wrap'>
                <span className='text-gray-500 w-14 shrink-0 font-medium'>API</span>
                <span className='font-mono text-gray-400'>{inst.private_api}</span>
                {inst.api_public && inst.public_api
                  ? (
                    <a
                      href={inst.public_api}
                      target='_blank'
                      rel='noopener'
                      className='flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400 font-mono hover:bg-emerald-900/50'
                    >
                      <Globe size={10} />
                      {inst.public_api.replace('https://', '')}
                    </a>
                  )
                  : (
                    <span className='flex items-center gap-1 text-gray-600'>
                      <Lock size={10} />
                      {t('apphost.private')}
                    </span>
                  )}
              </div>
              {/* Domain (volitelná vlastní doména) */}
              {inst.domain && (
                <div className='flex items-center gap-2 flex-wrap'>
                  <span className='text-gray-500 w-14 shrink-0 font-medium'>
                    {t('apphost.domain')}
                  </span>
                  <a
                    href={`https://${inst.domain}`}
                    target='_blank'
                    rel='noopener'
                    className='font-mono text-blue-400 hover:text-blue-300'
                  >
                    {inst.domain}
                  </a>
                  <span className='text-[10px] text-gray-600'>nginx: {inst.nginx_mode}</span>
                </div>
              )}
              {
                /* Náhled (blue) — zapnutí/vypnutí a adresa. Publikuje se stejnou
                  cestou jako ostatní adresy; vypnutí nemaže identitu. */
              }
              <PreviewUrlRow inst={inst} />
            </div>
          </CardContent>
        </Card>
      )}

      {inst.type === 'react' && (
        <ReleaseCarousel
          /* Projektová část: obecné `{name:…}` guard zamítne (`-32603`),
             legální je jedině `<projekt>.apphost.release_switch` se `service`.
             Dřív tady stál jen ten obecný tvar, takže karta nad projektovou
             instancí dostala chybu a tvářila se, že data neexistují —
             „přepnutí proběhlo, ale stav se nerefreshnul" (keen-comet,
             acm-dev 2026-09-10). */
          tool={inst.project
            ? `${inst.project.id}.apphost.release_switch`
            : 'apphost.release_switch'}
          args={inst.project ? { service: inst.project.service } : { name: inst.name }}
          callTool={(tool, args) => callMcp(tool, args)}
          liveUrl={inst.domain ? `https://${inst.domain}` : null}
          previewUrl={inst.preview_enabled && inst.preview_domain
            ? `https://${inst.preview_domain}`
            : null}
        />
      )}
    </div>
  );
}

/** Náhledová (blue) URL: přepínač + adresa. Jen pro react instance. */
function PreviewUrlRow({ inst }: { inst: AppInstance }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  if (inst.type !== 'react') return null;

  const setPreview = async (enabled: boolean) => {
    setBusy(true);
    try {
      await api.mcp('apphost.preview_set', { name: inst.name, enabled });
      toast.success(
        enabled
          ? t('apphost.previewOn', 'Náhledová URL zapnuta')
          : t('apphost.previewOff', 'Náhledová URL vypnuta'),
      );
      qc.invalidateQueries({ queryKey: ['apphost-instances'] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='flex items-center gap-2 flex-wrap'>
      <span className='text-gray-500 w-14 shrink-0 font-medium'>
        {t('apphost.preview', 'Náhled')}
      </span>
      <Toggle
        value={inst.preview_enabled === true}
        onSave={(v) => setPreview(v)}
        disabled={busy}
        label={''}
      />
      {inst.preview_enabled && inst.preview_domain
        ? (
          <a
            href={`https://${inst.preview_domain}`}
            target='_blank'
            rel='noopener'
            className='font-mono text-amber-400 hover:text-amber-300'
          >
            {inst.preview_domain}
          </a>
        )
        : (
          <span className='text-gray-600 text-[11px]'>
            {inst.preview_domain
              ? t('apphost.previewOffKeeps', 'vypnuto — adresa zůstává přidělená')
              : t('apphost.previewNone', 'nepřidělena')}
          </span>
        )}
      {inst.preview_port && (
        <span className='text-[10px] text-gray-600'>port {inst.preview_port}</span>
      )}
    </div>
  );
}

function ConfigPanel({ name }: { name: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showSecrets, setShowSecrets] = useState(false);
  const [editKey, setEditKey] = useState('');
  const [editVal, setEditVal] = useState('');
  const [isSecret, setIsSecret] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{
    ok: boolean;
    name: string;
    env_config: Record<string, string>;
    env_secrets: Record<string, string>;
    all_keys: string[];
    computed_env: Record<string, string>;
    dbhost_link: { id: string; db: string; user: string } | null;
  }>({
    queryKey: ['apphost-config', name, showSecrets],
    queryFn: () =>
      callMcp('apphost.config.get', {
        name,
        secrets: showSecrets,
      }),
  });

  const saveMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => callMcp('apphost.config.set', { name, ...body }),
    onSuccess: () => {
      toast.success(t('apphost.saved'));
      qc.invalidateQueries({ queryKey: ['apphost-config', name] });
      setEditingKey(null);
      setEditKey('');
      setEditVal('');
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: (key: string) => callMcp('apphost.config.set', { name, delete_keys: [key] }),
    onSuccess: () => {
      toast.success(t('apphost.keyDeleted'));
      qc.invalidateQueries({ queryKey: ['apphost-config', name] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const handleExport = () =>
    window.open(`/apiext/apphost/instances/${name}/config/export`, '_blank');

  if (isLoading) {
    return (
      <div className='space-y-3'>
        <div className='h-8 bg-gray-800/40 rounded-xl animate-pulse' />
        <div className='h-48 bg-gray-800/40 rounded-xl animate-pulse' />
      </div>
    );
  }

  const allKeys = [
    ...Object.entries(data?.env_config ?? {}).map(([k, v]) => ({ k, v, secret: false })),
    ...Object.entries(data?.env_secrets ?? {}).map(([k, v]) => ({ k, v, secret: true })),
  ];

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <Toggle
          value={showSecrets}
          onSave={(v) => setShowSecrets(v)}
          label={t('apphost.showSecrets')}
        />
        <div className='ml-auto flex gap-2'>
          <Button
            variant='secondary'
            size='sm'
            icon={<Download size={15} />}
            onClick={handleExport}
          >
            {t('apphost.exportEnv')}
          </Button>
          <Button
            variant='primary'
            size='sm'
            icon={<Plus size={15} />}
            onClick={() => {
              setEditingKey('__new__');
              setEditKey('');
              setEditVal('');
              setIsSecret(false);
            }}
          >
            {t('apphost.add')}
          </Button>
        </div>
      </div>

      {editingKey === '__new__' && (
        <Card>
          <CardContent className='p-4 space-y-3'>
            <div className='grid grid-cols-2 gap-3'>
              <Input
                size='sm'
                value={editKey}
                onChange={(e) => setEditKey(e.target.value)}
                placeholder={t('apphost.keyPlaceholder')}
                className='font-mono'
              />
              <Input
                size='sm'
                value={editVal}
                onChange={(e) => setEditVal(e.target.value)}
                placeholder={t('apphost.value')}
                type={isSecret ? 'password' : 'text'}
              />
            </div>
            <div className='flex items-center gap-3'>
              <Toggle
                value={isSecret}
                onSave={(v) => setIsSecret(v)}
                label={t('apphost.secretValue')}
              />
              <div className='ml-auto flex gap-2'>
                <Button variant='ghost' size='sm' onClick={() => setEditingKey(null)}>
                  {t('apphost.cancel')}
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() =>
                    saveMut.mutate(
                      isSecret
                        ? { secrets: { [editKey]: editVal } }
                        : { env: { [editKey]: editVal } },
                    )}
                  disabled={!editKey || !editVal}
                  loading={saveMut.isPending}
                >
                  {t('apphost.save')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className='border border-gray-800 rounded-xl overflow-hidden'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b border-gray-800 bg-gray-900/30'>
              <th className='text-left px-4 py-2.5 text-xs font-medium text-gray-600 w-8'></th>
              <th className='text-left px-4 py-2.5 text-xs font-medium text-gray-600'>
                {t('apphost.key')}
              </th>
              <th className='text-left px-4 py-2.5 text-xs font-medium text-gray-600'>
                {t('apphost.value')}
              </th>
              <th className='px-4 py-2.5 w-16'></th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-800/40'>
            {allKeys.length === 0 && (
              <tr>
                <td colSpan={4}>
                  <EmptyState
                    icon={<Settings size={24} />}
                    title={t('apphost.noVariables')}
                    className='py-8'
                  />
                </td>
              </tr>
            )}
            {allKeys.map(({ k, v, secret }) => (
              <tr key={k} className='hover:bg-gray-800/20 group'>
                <td className='px-4 py-2'>
                  {secret && (
                    <span title={t('apphost.secret')}>
                      <Lock size={12} className='text-amber-400' />
                    </span>
                  )}
                </td>
                <td className='px-4 py-2'>
                  <code className='text-xs font-mono text-gray-300'>{k}</code>
                </td>
                <td className='px-4 py-2'>
                  {editingKey === k
                    ? (
                      <div className='flex gap-2'>
                        <Input
                          size='sm'
                          defaultValue={v === '****' ? '' : v}
                          id={`edit-${k}`}
                          type={secret ? 'password' : 'text'}
                          className='flex-1 font-mono'
                        />
                        <Button
                          variant='primary'
                          size='sm'
                          onClick={() => {
                            const val =
                              (document.getElementById(`edit-${k}`) as HTMLInputElement).value;
                            saveMut.mutate(
                              secret ? { secrets: { [k]: val } } : { env: { [k]: val } },
                            );
                          }}
                        >
                          OK
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => setEditingKey(null)}
                          icon={<X size={13} />}
                        />
                      </div>
                    )
                    : (
                      <code
                        className={`text-xs font-mono ${
                          secret && v === '****' ? 'text-gray-600' : 'text-green-400'
                        }`}
                        onDoubleClick={() => setEditingKey(k)}
                      >
                        {v}
                      </code>
                    )}
                </td>
                <td className='px-4 py-2'>
                  <div className='opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 justify-end'>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={() => setEditingKey(k)}
                      icon={<Pencil size={12} />}
                      className='hover:text-blue-400'
                    />
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={async () =>
                        await themedConfirm(t('apphost.confirmDeleteKey', { key: k })) &&
                        deleteMut.mutate(k)}
                      icon={<Trash2 size={12} />}
                      className='hover:text-red-400'
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GitPanel({ name, inst, onDeploy, deploying }: {
  name: string;
  inst: AppInstance & { git?: Record<string, unknown> };
  onDeploy: (body: Record<string, unknown>) => void;
  deploying: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [gitForm, setGitForm] = useState({
    repo: (inst.git?.repo as string) ?? inst.git_repo ?? '',
    branch: inst.git_branch ?? 'main',
    token: '',
  });

  const saveMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => callMcp('apphost.config.set', { name, ...body }),
    onSuccess: () => {
      toast.success(t('apphost.gitConfigSaved'));
      qc.invalidateQueries({ queryKey: ['apphost-status', scopeKey(), name] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='space-y-5 max-w-lg'>
      <Card>
        <CardHeader>
          <CardTitle icon={GitBranch}>{t('apphost.gitConfig')}</CardTitle>
        </CardHeader>
        <CardContent className='p-4 space-y-3'>
          <div>
            <Label>{t('apphost.repositoryUrl')}</Label>
            <Input
              size='sm'
              value={gitForm.repo}
              onChange={(e) => setGitForm((f) => ({ ...f, repo: e.target.value }))}
              placeholder='https://gitlab.example.com/user/repo.git'
              className='font-mono'
            />
          </div>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <Label>{t('apphost.branch')}</Label>
              <Input
                size='sm'
                value={gitForm.branch}
                onChange={(e) => setGitForm((f) => ({ ...f, branch: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('apphost.accessToken')}</Label>
              <Input
                size='sm'
                value={gitForm.token}
                onChange={(e) => setGitForm((f) => ({ ...f, token: e.target.value }))}
                type='password'
                placeholder='glpat-…'
              />
            </div>
          </div>
          <Button
            variant='secondary'
            size='sm'
            onClick={() =>
              saveMut.mutate({
                env: { git_branch: gitForm.branch },
                secrets: {
                  ...(gitForm.token ? { git_token: gitForm.token } : {}),
                  ...(gitForm.repo ? { git_repo: gitForm.repo } : {}),
                },
              })}
            loading={saveMut.isPending}
          >
            {saveMut.isPending ? t('apphost.saving') : t('apphost.saveConfig')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle icon={Upload}>Deploy</CardTitle>
        </CardHeader>
        <CardContent className='p-4'>
          <Button
            variant='primary'
            size='md'
            className='w-full'
            onClick={() =>
              onDeploy({
                git_repo: gitForm.repo || undefined,
                git_branch: gitForm.branch || undefined,
                git_token: gitForm.token || undefined,
              })}
            loading={deploying}
            icon={<GitBranch size={15} />}
          >
            {deploying ? t('apphost.deploying') : t('apphost.deployFromGit')}
          </Button>
          {deploying && (
            <div className='mt-3 text-xs text-gray-500 text-center animate-pulse'>
              {t('apphost.deployFlow')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ExecPanel({ name, type }: { name: string; type: AppType }) {
  const { t } = useTranslation();
  const [cmd, setCmd] = useState(type === 'laravel' ? 'php artisan ' : 'npm run ');
  const [output, setOutput] = useState('');
  const [running, setRunning] = useState(false);

  const PRESETS: Record<AppType, { label: string; cmd: string }[]> = {
    laravel: [
      { label: 'migrate', cmd: 'php artisan migrate --force' },
      { label: 'migrate:fresh', cmd: 'php artisan migrate:fresh --seed --force' },
      { label: 'cache:clear', cmd: 'php artisan optimize:clear' },
      { label: 'queue:restart', cmd: 'php artisan queue:restart' },
      { label: 'storage:link', cmd: 'php artisan storage:link' },
      { label: 'composer install', cmd: 'composer install --no-dev --optimize-autoloader' },
    ],
    react: [
      { label: 'install', cmd: 'npm ci' },
      { label: 'build', cmd: 'npm run build' },
      { label: 'test', cmd: 'npm test -- --watchAll=false' },
    ],
    node: [
      { label: 'install', cmd: 'npm ci' },
      { label: 'build', cmd: 'npm run build' },
      { label: 'start', cmd: 'npm start' },
    ],
  };

  const run = async () => {
    if (!cmd.trim()) return;
    setRunning(true);
    setOutput('');
    try {
      const r = await callMcp<{
        ok: boolean;
        exit_code?: number;
        stdout?: string;
        stderr?: string;
      }>(
        'apphost.exec',
        { name, command: cmd },
      );
      setOutput(
        [
          r.ok ? t('apphost.execDone') : t('apphost.execExitCode', { code: r.exit_code }),
          r.stdout && `\n--- stdout ---\n${r.stdout}`,
          r.stderr && `\n--- stderr ---\n${r.stderr}`,
        ].filter(Boolean).join(''),
      );
    } catch (e) {
      setOutput(t('apphost.execError', { message: (e as Error).message }));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap gap-1.5'>
        {PRESETS[type].map((p) => (
          <Button
            key={p.cmd}
            variant='secondary'
            size='sm'
            onClick={() => setCmd(p.cmd)}
            className='font-mono'
          >
            {p.label}
          </Button>
        ))}
      </div>
      <div className='flex gap-2'>
        <Input
          value={cmd}
          onChange={(e) => setCmd(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && run()}
          className='flex-1 font-mono'
          placeholder='php artisan migrate'
        />
        <Button
          variant='secondary'
          size='md'
          onClick={run}
          disabled={!cmd.trim()}
          loading={running}
          icon={<Terminal size={15} />}
        >
          {t('apphost.run')}
        </Button>
      </div>
      {output && (
        <pre className='bg-gray-950 border border-gray-800 rounded-xl p-4 text-xs font-mono text-green-400 overflow-x-auto max-h-80 overflow-y-auto whitespace-pre-wrap'>
          {output}
        </pre>
      )}
    </div>
  );
}

function LogsPanel({ name }: { name: string }) {
  const { t } = useTranslation();
  const [service, setService] = useState<'app' | 'redis' | 'db'>('app');
  const [lines, setLines] = useState(100);

  const { data, isLoading, refetch } = useQuery<{
    ok: boolean;
    error?: string;
    logs?: string;
    container?: string;
    lines?: number;
  }>({
    queryKey: ['apphost-logs', name, service, lines],
    queryFn: () =>
      callMcp('apphost.logs', {
        name,
        service,
        lines,
      }),
  });

  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-2'>
        {(['app', 'redis', 'db'] as const).map((s) => (
          <Button
            key={s}
            variant={service === s ? 'secondary' : 'ghost'}
            size='sm'
            onClick={() => setService(s)}
            className='font-mono'
          >
            {s}
          </Button>
        ))}
        <Select
          size='sm'
          value={lines}
          onChange={(e) => setLines(Number(e.target.value))}
          options={[50, 100, 200, 500].map((n) => ({
            value: String(n),
            label: t('apphost.lines', { count: n }),
          }))}
          className='ml-auto w-36'
        />
        <RefreshButton onRefresh={() => refetch()} size='md' />
      </div>
      {isLoading && <div className='h-64 bg-gray-800/40 rounded-xl animate-pulse' />}
      {data && (
        <pre className='bg-gray-950 border border-gray-800 rounded-xl p-4 text-xs font-mono text-gray-300 overflow-x-auto max-h-[500px] overflow-y-auto whitespace-pre-wrap'>
          {data.logs || t('apphost.emptyLogs')}
        </pre>
      )}
    </div>
  );
}

// ── Create Wizard ─────────────────────────────────────────────────────────────

function CreateWizard({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: '',
    type: 'laravel' as AppType,
    db_mode: 'postgres' as DbMode,
    db_url: '',
    redis_mode: 'container' as RedisMode,
    redis_url: '',
    nginx_mode: 'npm' as NginxMode,
    domain: '',
    git_repo: '',
    git_branch: 'main',
    git_token: '',
    start: true,
  });

  const complex = (form.db_mode === 'postgres' || form.db_mode === 'mysql') ||
    !!form.git_repo.trim();
  const mut = useMutation({
    mutationFn: () => {
      if (complex) {
        const backendType = form.db_mode === 'postgres'
          ? 'postgres'
          : form.db_mode === 'mysql'
          ? 'mysql'
          : 'none';
        return callMcp('orchestrator.project_create', {
          request: {
            version: 1,
            source: { consumer: 'apphost', channel: 'ui-create' },
            project: { slug: form.name },
            repository: {
              provider: 'gitlab',
              mode: 'create',
              visibility: 'internal',
              initialize_with_readme: !form.git_repo,
            },
            template: form.git_repo
              ? { kind: 'repository', source_url: form.git_repo }
              : { kind: 'builtin', name: form.type === 'react' ? 'react-vite' : 'react-vite' },
            runtime: {
              frontend: { type: form.type, run_mode: form.start ? 'dev' : 'build' },
              backend: { type: backendType },
            },
            publication: {
              frontend: {
                enabled: form.nginx_mode !== 'none',
                ...(form.domain ? { domain: form.domain } : {}),
              },
            },
            codable: { enabled: false },
          },
        });
      }
      return callMcp('apphost.create', {
        name: form.name,
        type: form.type,
        db_mode: form.db_mode,
        db_url: form.db_mode === 'external' ? form.db_url : undefined,
        redis_mode: form.redis_mode,
        redis_url: form.redis_mode === 'shared' ? form.redis_url : undefined,
        nginx_mode: form.nginx_mode,
        domain: form.domain || undefined,
        git_repo: form.git_repo || undefined,
        git_branch: form.git_branch,
        git_token: form.git_token || undefined,
        start: form.start,
      });
    },
    onSuccess: (r: unknown) => {
      const res = r as { ok?: boolean; operation_id?: string; error?: string };
      if (complex && res.operation_id) {
        toast.success(
          t('apphost.orchStarted', 'Orchestrátor zakládá stack {{name}}', { name: form.name }),
        );
        onCreated();
        return;
      }
      if (res?.ok === false) {
        toast.error(res.error ?? t('apphost.createFailed', 'Vytvoření selhalo'));
        return;
      }
      toast.success(t('apphost.instanceCreated', { name: form.name }));
      onCreated();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const steps = [
    t('apphost.stepBasic'),
    t('apphost.stepDatabase'),
    t('apphost.stepProxyDomain'),
    t('apphost.stepGit'),
    t('apphost.stepStart'),
  ];

  return (
    <Modal open onClose={onClose} title={t('apphost.newInstance')}>
      <div>
        {/* Step indicators */}
        <div className='flex items-center gap-1 mb-6'>
          {steps.map((s, i) => (
            <div key={s} className='flex items-center gap-1'>
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                  i <= step ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-600'
                }`}
              >
                {i + 1}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`h-0.5 w-8 transition-colors ${
                    i < step ? 'bg-blue-600' : 'bg-gray-800'
                  }`}
                />
              )}
            </div>
          ))}
          <span className='ml-auto text-xs text-gray-500'>{steps[step]}</span>
        </div>

        <div className='space-y-4 min-h-[180px]'>
          {step === 0 && (
            <>
              <div>
                <Label>{t('apphost.instanceName')} *</Label>
                <Input
                  size='sm'
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
                    }))}
                  placeholder='my-app'
                />
                <div className='text-[10px] text-gray-600 mt-1'>
                  {t('apphost.slugHint')}
                </div>
              </div>
              <div>
                <div className='text-xs text-gray-400 mb-2'>{t('apphost.appType')}</div>
                <div className='grid grid-cols-3 gap-2'>
                  {(['laravel', 'react', 'node'] as AppType[]).map((tp) => (
                    <button
                      key={tp}
                      onClick={() => setForm((f) => ({ ...f, type: tp }))}
                      className={`py-3 rounded-xl border text-sm font-medium transition-colors ${
                        form.type === tp
                          ? 'border-blue-500/50 bg-blue-600/15 text-blue-400'
                          : 'border-gray-700 text-gray-500 hover:text-white'
                      }`}
                    >
                      {tp}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div>
                <div className='text-xs text-gray-400 mb-2'>{t('apphost.database')}</div>
                <div className='grid grid-cols-2 gap-2 mb-3'>
                  {(['none', 'postgres', 'mysql', 'external'] as DbMode[]).map((m) => (
                    <button
                      key={m}
                      onClick={() => setForm((f) => ({ ...f, db_mode: m }))}
                      className={`py-2 rounded-lg border text-xs font-medium transition-colors ${
                        form.db_mode === m
                          ? 'border-blue-500/50 bg-blue-600/15 text-blue-400'
                          : 'border-gray-700 text-gray-500 hover:text-white'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                {form.db_mode === 'external' && (
                  <Input
                    size='sm'
                    value={form.db_url}
                    onChange={(e) => setForm((f) => ({ ...f, db_url: e.target.value }))}
                    placeholder='postgres://user:pass@host:5432/db'
                    className='font-mono'
                  />
                )}
              </div>
              <div>
                <div className='text-xs text-gray-400 mb-2'>{t('apphost.redis')}</div>
                <div className='grid grid-cols-3 gap-2 mb-3'>
                  {(['none', 'container', 'shared'] as RedisMode[]).map((m) => (
                    <button
                      key={m}
                      onClick={() => setForm((f) => ({ ...f, redis_mode: m }))}
                      className={`py-2 rounded-lg border text-xs font-medium transition-colors ${
                        form.redis_mode === m
                          ? 'border-blue-500/50 bg-blue-600/15 text-blue-400'
                          : 'border-gray-700 text-gray-500 hover:text-white'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                {form.redis_mode === 'shared' && (
                  <Input
                    size='sm'
                    value={form.redis_url}
                    onChange={(e) => setForm((f) => ({ ...f, redis_url: e.target.value }))}
                    placeholder='redis://host:6379'
                    className='font-mono'
                  />
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <div className='text-xs text-gray-400 mb-2'>{t('apphost.nginxProxy')}</div>
                <div className='grid grid-cols-3 gap-2 mb-3'>
                  {(['npm', 'internal', 'none'] as NginxMode[]).map((m) => (
                    <button
                      key={m}
                      onClick={() => setForm((f) => ({ ...f, nginx_mode: m }))}
                      className={`py-2 rounded-lg border text-xs font-medium transition-colors ${
                        form.nginx_mode === m
                          ? 'border-blue-500/50 bg-blue-600/15 text-blue-400'
                          : 'border-gray-700 text-gray-500 hover:text-white'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                <div className='text-[10px] text-gray-600'>
                  {form.nginx_mode === 'npm' && t('apphost.nginxNpmHint')}
                  {form.nginx_mode === 'internal' && t('apphost.nginxInternalHint')}
                  {form.nginx_mode === 'none' && t('apphost.nginxNoneHint')}
                </div>
              </div>
              {form.nginx_mode !== 'none' && (
                <div>
                  <Label>{t('apphost.domain')}</Label>
                  <Input
                    size='sm'
                    value={form.domain}
                    onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                    placeholder='myapp.services.maxcloud.cz'
                  />
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <div>
                <Label>{t('apphost.gitRepositoryUrl')}</Label>
                <Input
                  size='sm'
                  value={form.git_repo}
                  onChange={(e) => setForm((f) => ({ ...f, git_repo: e.target.value }))}
                  placeholder='https://gitlab.example.com/user/repo.git'
                  className='font-mono'
                />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label>{t('apphost.branch')}</Label>
                  <Input
                    size='sm'
                    value={form.git_branch}
                    onChange={(e) => setForm((f) => ({ ...f, git_branch: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>{t('apphost.accessToken')}</Label>
                  <Input
                    size='sm'
                    value={form.git_token}
                    onChange={(e) => setForm((f) => ({ ...f, git_token: e.target.value }))}
                    type='password'
                    placeholder='glpat-…'
                  />
                </div>
              </div>
              <div className='text-[10px] text-gray-600'>
                {t('apphost.gitOptionalHint')}
              </div>
            </>
          )}

          {step === 4 && (
            <div className='space-y-4'>
              {complex && (
                <div className='rounded-xl border border-gray-800 bg-gray-950/40 p-4'>
                  <StackFlowGraphic
                    title={t('apphost.orchStackHint', 'Složitý stack — založí Orchestrátor')}
                    services={plannedStack({
                      frontend: form.type,
                      backend: form.db_mode === 'postgres'
                        ? 'postgres'
                        : form.db_mode === 'mysql'
                        ? 'mysql'
                        : 'none',
                      publish: form.nginx_mode !== 'none',
                      backup: form.db_mode !== 'none',
                      gitHint: form.git_repo.split('/').pop()?.replace(/\.git$/i, ''),
                    })}
                  />
                </div>
              )}
              <div className='bg-gray-800/50 border border-gray-700 rounded-xl p-4 space-y-2 text-xs'>
                {[
                  [t('apphost.summaryName'), form.name],
                  [t('apphost.summaryType'), form.type],
                  [t('apphost.summaryDatabase'), form.db_mode],
                  [t('apphost.summaryRedis'), form.redis_mode],
                  [t('apphost.summaryNginx'), form.nginx_mode],
                  [t('apphost.summaryDomain'), form.domain || '—'],
                  [t('apphost.summaryGitRepo'), form.git_repo || '—'],
                ].map(([k, v]) => (
                  <div key={k} className='flex justify-between'>
                    <span className='text-gray-500'>{k}</span>
                    <span className='text-gray-300 font-mono'>{v}</span>
                  </div>
                ))}
              </div>
              <Toggle
                value={form.start}
                onSave={(v) => setForm((f) => ({ ...f, start: v }))}
                label={t('apphost.startContainersNow')}
              />
            </div>
          )}
        </div>

        <div className='flex gap-2 mt-6'>
          <Button variant='secondary' size='md' onClick={onClose}>
            {t('apphost.cancel')}
          </Button>
          {step > 0 && (
            <Button variant='secondary' size='md' onClick={() => setStep((s) => s - 1)}>
              ← {t('apphost.back')}
            </Button>
          )}
          <div className='flex-1' />
          {step < steps.length - 1 && (
            <Button
              variant='primary'
              size='md'
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 0 && !form.name}
            >
              {t('apphost.next')} →
            </Button>
          )}
          {step === steps.length - 1 && (
            <Button
              variant='success'
              size='md'
              onClick={() => mut.mutate()}
              disabled={!form.name}
              loading={mut.isPending}
            >
              {mut.isPending ? t('apphost.creating') : t('apphost.createInstance')}
            </Button>
          )}
        </div>
        {mut.error && (
          <p className='mt-2 text-xs text-red-400 flex items-center gap-1'>
            <AlertCircle size={12} />
            {(mut.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ── Plugin export — pro orchestrator/infrastruktura přes servicePlugins ─────
export function AppHostServiceEditor(
  { name, onClose }: { name: string; instance?: string; kind?: string; onClose: () => void },
) {
  return <InstanceDetail name={name} onBack={onClose} />;
}

// ── Main ──────────────────────────────────────────────────────────────────────

function InstancesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['apphost-list', scopeKey()],
    queryFn: () =>
      callMcp('apphost.list', {}) as Promise<{
        ok: boolean;
        instances: AppInstance[];
        total: number;
      }>,
    refetchInterval: 30_000,
  });

  const instances = data?.instances ?? [];
  const running = instances.filter((i) => i.status === 'running').length;

  if (selected) {
    return <InstanceDetail name={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div className='space-y-6'>
      <StatsGrid>
        <StatCard label={t('apphost.totalInstances')} value={instances.length} icon={Server} />
        <StatCard
          label={t('apphost.runningCount')}
          value={running}
          icon={CheckCircle2}
          variant={running > 0 ? 'success' : 'default'}
        />
        <StatCard
          label={t('apphost.typeLaravel', 'Laravel')}
          value={instances.filter((i) => i.type === 'laravel').length}
          icon={Layers}
        />
        <StatCard
          label={t('apphost.typeReactNode', 'React / Node')}
          value={instances.filter((i) => i.type !== 'laravel').length}
          icon={Globe}
        />
      </StatsGrid>

      <div className='flex justify-end gap-2 mb-4'>
        <Button
          variant='primary'
          size='md'
          icon={<Plus size={15} />}
          onClick={() => setShowCreate(true)}
        >
          {t('apphost.newInstance')}
        </Button>
      </div>

      {isLoading && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
          {[0, 1, 2].map((i) => (
            <div key={i} className='h-36 bg-gray-800/40 rounded-xl animate-pulse' />
          ))}
        </div>
      )}
      {error && (
        <div className='flex gap-2 items-center text-red-400 text-sm p-4 bg-red-500/10 rounded-xl border border-red-500/20'>
          <AlertCircle size={15} />
          {(error as Error).message}
        </div>
      )}
      {!isLoading && instances.length === 0 && !error && (
        <EmptyState
          icon={<Server size={24} />}
          title={t('apphost.noInstancesHint')}
          action={
            <Button
              variant='primary'
              size='sm'
              icon={<Plus size={14} />}
              onClick={() => setShowCreate(true)}
            >
              {t('apphost.newInstance')}
            </Button>
          }
        />
      )}
      {instances.length > 0 && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
          {instances.map((inst) => (
            <InstanceCard
              key={inst.name}
              inst={inst}
              onSelect={() => setSelected(inst.name)}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateWizard
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['apphost-list', scopeKey()] });
          }}
        />
      )}
    </div>
  );
}

// ── InstanceLovablePanel — per-instance Lovable binding view ─────────────────
// Volá apphost.lovable.list, filtruje na instanci. Pokud žádný binding, ukáže
// note "Tato instance není napárovaná na Lovable import". Jinak: git_repo,
// supabase URL, anon key prefix, tier/state, last refresh, refresh_build action.


function InstanceLovablePanel({ name }: { name: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const listQ = useQuery({
    queryKey: ['lovable-list-for-instance', name],
    queryFn: async () => {
      const r = await callMcp<{ ok: boolean; count: number; items?: LovableBinding[] }>(
        'apphost.lovable.list',
        {},
      );
      return (r.items ?? []).find((it) => it.apphost_name === name) ?? null;
    },
    staleTime: 15_000,
  });

  const refreshMut = useMutation({
    mutationFn: () => callMcp('apphost.lovable.refresh_build', { apphost_name: name }),
    onSuccess: (r) => {
      const ok = (r as { ok?: boolean })?.ok;
      if (ok) {
        toast.success(t('apphost.buildRefreshOk'));
        qc.invalidateQueries({ queryKey: ['lovable-list-for-instance', name] });
        qc.invalidateQueries({ queryKey: ['apphost-status', scopeKey(), name] });
      } else {
        toast.error((r as { error?: string })?.error ?? t('apphost.refreshFailed'));
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (listQ.isLoading) {
    return (
      <div className='space-y-3'>
        <div className='h-8 bg-gray-800/40 rounded-xl animate-pulse' />
        <div className='h-40 bg-gray-800/40 rounded-xl animate-pulse' />
      </div>
    );
  }

  const binding = listQ.data;
  if (!binding) {
    return (
      <Card>
        <CardContent className='p-4'>
          <div className='flex items-start gap-3'>
            <Wand2 size={16} className='text-gray-600 mt-0.5 flex-shrink-0' />
            <div className='text-sm text-gray-400'>
              <div className='text-gray-300 mb-1'>{t('apphost.notPairedTitle')}</div>
              <div className='text-xs text-gray-500'>
                {t('apphost.notPairedHelpPre')}{' '}
                <code className='text-amber-400'>apphost.lovable.adopt</code>{' '}
                {t('apphost.notPairedHelpOr')}{' '}
                <code className='text-amber-400'>apphost.lovable.provision</code>{' '}
                {t('apphost.notPairedHelpPost')} <strong>Lovable</strong>{' '}
                {t('apphost.notPairedHelpTab')}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const repoUrlSafe = binding.origin_repo?.replace(/:\/\/([^@]+)@/, '://***@') ?? null;
  return (
    <div className='space-y-4'>
      {/* Header s tier + state */}
      <div className='flex items-center gap-2'>
        <Wand2 size={14} className='text-amber-400' />
        <span className='text-sm font-semibold text-white'>Lovable binding</span>
        <Badge
          variant={binding.tier === 'production'
            ? 'red'
            : binding.tier === 'staging'
            ? 'yellow'
            : 'gray'}
        >
          {binding.tier}
        </Badge>
        <Badge variant={binding.state === 'deployed' ? 'green' : 'gray'}>{binding.state}</Badge>
        {binding.is_adopted && <Badge variant='blue'>adopted</Badge>}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
        {/* Git */}
        <Card>
          <CardHeader>
            <CardTitle icon={GitBranch}>Git repository</CardTitle>
          </CardHeader>
          <CardContent className='space-y-2 py-3'>
            <div>
              <div className='text-[10px] text-gray-500'>Origin</div>
              <code className='text-xs text-white font-mono break-all'>{repoUrlSafe ?? '—'}</code>
            </div>
            <div className='flex gap-4 text-xs'>
              <div>
                <span className='text-gray-500'>branch:</span>{' '}
                <code className='text-amber-300'>{binding.git_branch ?? '—'}</code>
              </div>
              <div>
                <span className='text-gray-500'>git_token:</span> {binding.has_git_token
                  ? <span className='text-emerald-400'>✓</span>
                  : <span className='text-gray-600'>—</span>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Supabase */}
        <Card>
          <CardHeader>
            <CardTitle icon={Database}>Supabase (Lovable cloud)</CardTitle>
          </CardHeader>
          <CardContent className='space-y-2 py-3'>
            <div>
              <div className='text-[10px] text-gray-500'>Project ID</div>
              <code className='text-xs text-white font-mono'>{binding.project_id}</code>
            </div>
            {binding.cloud_supabase_url && (
              <div>
                <div className='text-[10px] text-gray-500'>URL</div>
                <a
                  href={binding.cloud_supabase_url}
                  target='_blank'
                  rel='noopener'
                  className='text-xs text-blue-400 hover:text-blue-300 break-all font-mono flex items-center gap-1'
                >
                  {binding.cloud_supabase_url} <ExternalLink size={9} />
                </a>
              </div>
            )}
            <div className='flex gap-4 text-xs'>
              <div>
                <span className='text-gray-500'>service_role:</span> {binding.has_service_role
                  ? <span className='text-emerald-400'>✓</span>
                  : <span className='text-gray-600'>—</span>}
              </div>
              <div>
                <span className='text-gray-500'>jwt_secret:</span> {binding.has_jwt_secret
                  ? <span className='text-emerald-400'>✓</span>
                  : <span className='text-gray-600'>—</span>}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Migrace + last pull */}
      <Card>
        <CardHeader>
          <CardTitle icon={Clock}>{t('apphost.importStatus')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-2 py-3'>
          <div className='grid grid-cols-3 gap-3 text-xs'>
            <div>
              <div className='text-[10px] text-gray-500'>{t('apphost.appliedMigrations')}</div>
              <div className='text-white'>{binding.applied_migrations_count}</div>
            </div>
            <div>
              <div className='text-[10px] text-gray-500'>{t('apphost.lastDataPull')}</div>
              <div className='text-white'>
                {binding.last_data_pull_at
                  ? new Date(binding.last_data_pull_at).toLocaleString('cs')
                  : '—'}
              </div>
            </div>
            <div>
              <div className='text-[10px] text-gray-500'>{t('apphost.updated')}</div>
              <div className='text-white'>{new Date(binding.updated_at).toLocaleString('cs')}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Akce */}
      <div className='flex gap-2'>
        <Button
          variant='primary'
          size='sm'
          onClick={async () => {
            if (
              await themedConfirm(
                t('apphost.confirmRefreshBuild', {
                  name,
                  branch: binding.git_branch ?? 'main',
                }),
              )
            ) {
              refreshMut.mutate();
            }
          }}
          loading={refreshMut.isPending}
          disabled={!binding.has_git_token}
          icon={<RefreshCw size={13} />}
        >
          {t('apphost.refreshBuild')}
        </Button>
        {!binding.has_git_token && (
          <span className='text-xs text-amber-400 self-center'>{t('apphost.gitTokenMissing')}</span>
        )}
      </div>
    </div>
  );
}

// ── Widget entrypoints ───────────────────────────────────────────────────────

export function InstancesPanel({ scope }: WidgetRenderProps) {
  setApphostWidgetScope(scope ?? { node: null, identity: null });
  return <InstancesTab key={scopeKey()} />;
}

export function LovablePanel({ scope }: WidgetRenderProps) {
  setApphostWidgetScope(scope ?? { node: null, identity: null });
  return (
    <div key={scopeKey()} className='space-y-4'>
      <LovableControlPanel />
    </div>
  );
}
